import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, CalendarPlus, Image, ArrowLeft, Trash, Check, X, ChartBar, CalendarBlank, QrCode, Cake } from "@phosphor-icons/react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [pendingPosts, setPendingPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Event form
  const [eventForm, setEventForm] = useState({
    title: "", description: "", date: "", time_start: "", time_end: "",
    location: "MARABU OPEN CLUB", category: "evento", team: "", max_attendees: 0,
  });
  const [creating, setCreating] = useState(false);

  // QR Validator
  const [qrInput, setQrInput] = useState("");
  const [qrResult, setQrResult] = useState(null);
  const [validating, setValidating] = useState(false);

  // Birthdays
  const [birthdays, setBirthdays] = useState([]);

  useEffect(() => {
    if (!user?.is_admin) { navigate("/dashboard"); return; }
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const h = { headers: getAuthHeaders(), withCredentials: true };
      const [s, m, e, g, bd] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/stats`, h),
        axios.get(`${BACKEND_URL}/api/admin/members`, h),
        axios.get(`${BACKEND_URL}/api/events/all`, h),
        axios.get(`${BACKEND_URL}/api/gallery/pending`, h),
        axios.get(`${BACKEND_URL}/api/admin/birthdays`, h).catch(() => ({ data: [] })),
      ]);
      setStats(s.data);
      setMembers(m.data);
      setEvents(e.data);
      setPendingPosts(g.data);
      setBirthdays(bd.data);
    } catch (err) {
      if (err.response?.status === 403) navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title || !eventForm.date || !eventForm.time_start) {
      toast.error("Preencha título, data e horário"); return;
    }
    setCreating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/events`, eventForm, {
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" }, withCredentials: true,
      });
      toast.success("Evento criado!");
      setEventForm({ title: "", description: "", date: "", time_start: "", time_end: "", location: "MARABU OPEN CLUB", category: "evento", team: "", max_attendees: 0 });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar evento");
    } finally {
      setCreating(false);
    }
  };

  const deleteEvent = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/events/${id}`, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Evento removido");
      loadData();
    } catch (err) { toast.error("Erro ao remover"); }
  };

  const approvePost = async (id) => {
    try {
      await axios.put(`${BACKEND_URL}/api/gallery/${id}/approve`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Post aprovado!");
      loadData();
    } catch (err) { toast.error("Erro"); }
  };

  const rejectPost = async (id) => {
    try {
      await axios.put(`${BACKEND_URL}/api/gallery/${id}/reject`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Post rejeitado");
      loadData();
    } catch (err) { toast.error("Erro"); }
  };

  const validateQR = async (code) => {
    const c = code.trim().toUpperCase();
    if (!c) return;
    setValidating(true);
    setQrResult(null);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/validate/${c}`);
      setQrResult(res.data);
    } catch (err) {
      setQrResult({ valid: false, message: "Erro ao validar" });
    } finally {
      setValidating(false);
    }
  };

  const markAsUsed = async (code) => {
    try {
      await axios.post(`${BACKEND_URL}/api/validate/${code}/use`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Ingresso marcado como usado!");
      setQrResult((prev) => ({ ...prev, valid: false, message: "Ingresso já utilizado" }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    }
  };

  const tabs = [
    { id: "stats", label: "Resumo", icon: ChartBar },
    { id: "qr", label: "Portaria", icon: QrCode },
    { id: "events", label: "Eventos", icon: CalendarPlus },
    { id: "gallery", label: "Galeria", icon: Image },
    { id: "birthdays", label: "Aniversários", icon: Cake },
    { id: "members", label: "Membros", icon: Users },
  ];

  if (loading) return <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center text-[#115E59]">Carregando...</div>;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="admin-panel">
      <header className="bg-[#042F2E] border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate("/dashboard")} variant="ghost" className="text-white/80 hover:text-white rounded-full" data-testid="admin-back">
              <ArrowLeft size={24} />
            </Button>
            <h1 className="text-xl font-black text-white">ADMIN - MARABU</h1>
          </div>
          <span className="text-sm text-white/70">{user?.name}</span>
        </div>
      </header>

      <div className="bg-[#042F2E]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto gap-1 py-2 no-scrollbar">
            {tabs.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  className={`flex items-center gap-2 px-5 py-3 rounded-full text-sm font-bold whitespace-nowrap transition-all ${
                    activeTab === t.id ? "bg-[#FF5A26] text-white" : "text-white/70 hover:text-white hover:bg-white/10"
                  }`}
                  data-testid={`admin-tab-${t.id}`}>
                  <Icon size={18} weight={activeTab === t.id ? "fill" : "duotone"} />
                  {t.label}
                  {t.id === "gallery" && pendingPosts.length > 0 && (
                    <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{pendingPosts.length}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* STATS */}
        {activeTab === "stats" && stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Membros", value: stats.total_members, color: "from-blue-500 to-cyan-500" },
              { label: "Compras", value: stats.total_purchases, color: "from-green-500 to-emerald-500" },
              { label: "Eventos", value: stats.total_events, color: "from-orange-500 to-red-500" },
              { label: "Galeria Pendente", value: stats.pending_gallery, color: "from-purple-500 to-pink-500" },
            ].map((s, i) => (
              <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl border border-[#E4DFD3] p-6 shadow-sm" data-testid={`stat-${s.label.toLowerCase()}`}>
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center mb-3`}>
                  <span className="text-white font-bold text-lg">{s.value}</span>
                </div>
                <p className="text-sm font-bold text-[#115E59]">{s.label}</p>
              </motion.div>
            ))}
          </div>
        )}

        {/* QR PORTARIA */}
        {activeTab === "qr" && (
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-2xl border border-[#E4DFD3] p-8 shadow-sm text-center">
              <QrCode size={48} weight="duotone" className="text-[#FF5A26] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[#042F2E] mb-2">Validar Ingresso</h3>
              <p className="text-sm text-[#115E59] mb-6">Digite o código do ingresso ou escaneie o QR Code</p>
              <form onSubmit={(e) => { e.preventDefault(); validateQR(qrInput); }} className="space-y-4">
                <Input value={qrInput} onChange={(e) => setQrInput(e.target.value.toUpperCase())}
                  placeholder="Ex: MARABU-XXXXXXXX-YYYYYY" className="rounded-xl border-[#E4DFD3] text-center font-mono text-lg"
                  data-testid="qr-validator-input" />
                <Button type="submit" disabled={validating || !qrInput.trim()}
                  className="w-full bg-[#FF5A26] hover:bg-[#E04818] text-white font-bold rounded-full py-5"
                  data-testid="qr-validate-btn">
                  {validating ? "Validando..." : "Validar Ingresso"}
                </Button>
              </form>

              {qrResult && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className={`mt-6 rounded-xl p-6 ${qrResult.valid ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}
                  data-testid="qr-result">
                  <div className={`text-3xl font-black mb-2 ${qrResult.valid ? "text-[#10B981]" : "text-red-500"}`}>
                    {qrResult.valid ? "VÁLIDO" : "INVÁLIDO"}
                  </div>
                  <p className="text-sm mb-4">{qrResult.message}</p>
                  {qrResult.ticket && (
                    <div className="text-left text-sm space-y-1">
                      <p><strong>Item:</strong> {qrResult.ticket.item_name}</p>
                      <p><strong>Cliente:</strong> {qrResult.ticket.payer_name}</p>
                      {qrResult.ticket.scheduled_date && <p><strong>Agendado:</strong> {qrResult.ticket.scheduled_date} {qrResult.ticket.scheduled_time}</p>}
                      <p><strong>Valor:</strong> {qrResult.ticket.amount === 0 ? "GRÁTIS" : `R$ ${qrResult.ticket.amount?.toFixed(2)}`}</p>
                    </div>
                  )}
                  {qrResult.valid && (
                    <Button onClick={() => markAsUsed(qrResult.ticket?.ticket_code || qrInput)}
                      className="mt-4 w-full bg-[#10B981] hover:bg-[#059669] text-white font-bold rounded-full py-4"
                      data-testid="mark-used-btn">
                      <Check size={20} weight="bold" className="mr-2" /> Dar Entrada
                    </Button>
                  )}
                </motion.div>
              )}
            </div>
          </div>
        )}

        {/* EVENTS */}
        {activeTab === "events" && (
          <div className="space-y-8">
            <div className="bg-white rounded-2xl border border-[#E4DFD3] p-6 shadow-sm">
              <h3 className="text-lg font-bold text-[#042F2E] mb-4">Criar Novo Evento</h3>
              <form onSubmit={handleCreateEvent} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label className="text-sm font-bold text-[#115E59]">Título</Label>
                  <Input value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                    placeholder="Ex: Torneio de Futsal" className="rounded-xl border-[#E4DFD3]" required data-testid="event-title" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-sm font-bold text-[#115E59]">Descrição</Label>
                  <Input value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                    placeholder="Detalhes do evento..." className="rounded-xl border-[#E4DFD3]" data-testid="event-desc" />
                </div>
                <div>
                  <Label className="text-sm font-bold text-[#115E59]">Data</Label>
                  <Input type="date" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                    className="rounded-xl border-[#E4DFD3]" required data-testid="event-date" />
                </div>
                <div>
                  <Label className="text-sm font-bold text-[#115E59]">Categoria</Label>
                  <select value={eventForm.category} onChange={(e) => setEventForm({ ...eventForm, category: e.target.value })}
                    className="w-full rounded-xl border border-[#E4DFD3] px-4 py-2.5 text-sm" data-testid="event-category">
                    <option value="treino">Treino</option>
                    <option value="jogo">Jogo</option>
                    <option value="torneio">Torneio</option>
                    <option value="evento_social">Evento Social</option>
                    <option value="evento">Outro</option>
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-bold text-[#115E59]">Início</Label>
                  <Input type="time" value={eventForm.time_start} onChange={(e) => setEventForm({ ...eventForm, time_start: e.target.value })}
                    className="rounded-xl border-[#E4DFD3]" required data-testid="event-time-start" />
                </div>
                <div>
                  <Label className="text-sm font-bold text-[#115E59]">Fim</Label>
                  <Input type="time" value={eventForm.time_end} onChange={(e) => setEventForm({ ...eventForm, time_end: e.target.value })}
                    className="rounded-xl border-[#E4DFD3]" data-testid="event-time-end" />
                </div>
                <div>
                  <Label className="text-sm font-bold text-[#115E59]">Local</Label>
                  <Input value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                    className="rounded-xl border-[#E4DFD3]" data-testid="event-location" />
                </div>
                <div>
                  <Label className="text-sm font-bold text-[#115E59]">Máx. Participantes (0 = ilimitado)</Label>
                  <Input type="number" value={eventForm.max_attendees} onChange={(e) => setEventForm({ ...eventForm, max_attendees: parseInt(e.target.value) || 0 })}
                    className="rounded-xl border-[#E4DFD3]" data-testid="event-max" />
                </div>
                <div className="sm:col-span-2">
                  <Button type="submit" disabled={creating}
                    className="w-full bg-[#FF5A26] hover:bg-[#E04818] text-white font-bold rounded-full py-5" data-testid="create-event-btn">
                    {creating ? "Criando..." : "Criar Evento"}
                  </Button>
                </div>
              </form>
            </div>

            <div>
              <h3 className="text-lg font-bold text-[#042F2E] mb-4">Eventos ({events.length})</h3>
              <div className="space-y-3">
                {events.map((ev) => (
                  <div key={ev.id} className="bg-white rounded-xl border border-[#E4DFD3] p-4 flex items-center justify-between"
                    data-testid={`admin-event-${ev.id}`}>
                    <div>
                      <p className="font-bold text-[#042F2E]">{ev.title}</p>
                      <p className="text-sm text-[#115E59]">{new Date(ev.date + "T12:00:00").toLocaleDateString("pt-BR")} - {ev.time_start} | {ev.attendees?.length || 0} confirmados</p>
                    </div>
                    <Button onClick={() => deleteEvent(ev.id)} variant="ghost" className="text-red-500 hover:bg-red-50 rounded-full" data-testid={`delete-event-${ev.id}`}>
                      <Trash size={20} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* GALLERY MODERATION */}
        {activeTab === "gallery" && (
          <div>
            <h3 className="text-lg font-bold text-[#042F2E] mb-4">Postagens Pendentes ({pendingPosts.length})</h3>
            {pendingPosts.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-[#E4DFD3]">
                <Check size={48} weight="duotone" className="text-[#10B981] mx-auto mb-3" />
                <p className="text-[#115E59]">Nenhuma postagem pendente</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {pendingPosts.map((post) => (
                  <div key={post.id} className="bg-white rounded-2xl border border-[#E4DFD3] overflow-hidden shadow-sm"
                    data-testid={`pending-post-${post.id}`}>
                    <div className="aspect-video bg-[#F4F1EA]">
                      {post.media_type === "video" ? (
                        <video src={post.media_data} className="w-full h-full object-cover" controls />
                      ) : (
                        <img src={post.media_data} alt={post.title} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="p-4">
                      <p className="font-bold text-[#042F2E]">{post.title}</p>
                      <p className="text-xs text-[#115E59] mt-1">Por: {post.user_name} | {new Date(post.created_at).toLocaleDateString("pt-BR")}</p>
                      {post.description && <p className="text-sm text-[#115E59] mt-2">{post.description}</p>}
                      <div className="flex gap-2 mt-4">
                        <Button onClick={() => approvePost(post.id)}
                          className="flex-1 bg-[#10B981] hover:bg-[#059669] text-white font-bold rounded-full" data-testid={`approve-${post.id}`}>
                          <Check size={18} className="mr-1" /> Aprovar
                        </Button>
                        <Button onClick={() => rejectPost(post.id)}
                          className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold rounded-full" data-testid={`reject-${post.id}`}>
                          <X size={18} className="mr-1" /> Rejeitar
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* BIRTHDAYS */}
        {activeTab === "birthdays" && (
          <div>
            <h3 className="text-lg font-bold text-[#042F2E] mb-4">Próximos Aniversários</h3>
            {birthdays.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-[#E4DFD3]">
                <Cake size={48} weight="duotone" className="text-[#E4DFD3] mx-auto mb-3" />
                <p className="text-[#115E59]">Nenhum membro informou data de nascimento</p>
              </div>
            ) : (
              <div className="space-y-3">
                {birthdays.map((b) => (
                  <div key={b.id} className={`bg-white rounded-xl border p-4 flex items-center justify-between ${b.days_until === 0 ? "border-[#FF5A26] bg-[#FFF4E6]" : "border-[#E4DFD3]"}`}
                    data-testid={`birthday-${b.id}`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${b.days_until === 0 ? "bg-[#FF5A26]" : b.days_until <= 7 ? "bg-yellow-500" : "bg-[#0891B2]"}`}>
                        <Cake size={22} weight="fill" className="text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-[#042F2E]">{b.name}</p>
                        <p className="text-xs text-[#115E59]">
                          {new Date(b.birth_date + "T12:00:00").toLocaleDateString("pt-BR")} — faz {b.age} anos
                        </p>
                        <p className="text-xs text-[#115E59]">{b.phone || b.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {b.days_until === 0 ? (
                        <span className="bg-[#FF5A26] text-white px-3 py-1 rounded-full text-xs font-bold">HOJE!</span>
                      ) : (
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${b.days_until <= 7 ? "bg-yellow-100 text-yellow-700" : "bg-blue-100 text-blue-700"}`}>
                          em {b.days_until} dia{b.days_until !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* MEMBERS */}
        {activeTab === "members" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#042F2E]">Membros ({members.length})</h3>
              <Button
                onClick={async () => {
                  if (!window.confirm("Tem certeza que deseja remover TODOS os cadastros (exceto admin)?")) return;
                  try {
                    const res = await axios.delete(`${BACKEND_URL}/api/admin/clear-users`, { headers: getAuthHeaders(), withCredentials: true });
                    toast.success(res.data.message);
                    loadData();
                  } catch (err) { toast.error("Erro ao limpar cadastros"); }
                }}
                className="bg-red-500 hover:bg-red-600 text-white font-bold rounded-full text-sm px-4 py-2"
                data-testid="clear-users-btn"
              >
                Limpar Cadastros
              </Button>
            </div>
            <div className="bg-white rounded-2xl border border-[#E4DFD3] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F1EA]">
                    <tr>
                      <th className="px-4 py-3 text-left font-bold text-[#115E59]">Nome</th>
                      <th className="px-4 py-3 text-left font-bold text-[#115E59]">Email</th>
                      <th className="px-4 py-3 text-left font-bold text-[#115E59]">Telefone</th>
                      <th className="px-4 py-3 text-left font-bold text-[#115E59]">Cadastro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m) => (
                      <tr key={m.id} className="border-t border-[#E4DFD3]" data-testid={`member-${m.id}`}>
                        <td className="px-4 py-3 font-semibold text-[#042F2E]">{m.name} {m.is_admin && <span className="text-xs bg-[#FF5A26] text-white px-2 py-0.5 rounded-full ml-2">Admin</span>}</td>
                        <td className="px-4 py-3 text-[#115E59]">{m.email}</td>
                        <td className="px-4 py-3 text-[#115E59]">{m.phone || "-"}</td>
                        <td className="px-4 py-3 text-[#115E59]">{new Date(m.created_at).toLocaleDateString("pt-BR")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
